/**
 * Nuxt Plugin:
 * - 服务端加载组件定义供 SSR 渲染使用
 * - 客户端在 hydration 之前独立请求 /api/cms/components，不再走 __NUXT_DATA__ payload
 *
 * 移除原来的 useState 序列化方式，避免 htmlTemplate/css/jsLogic 等大字段
 * 被内联进 HTML 文档，改为浏览器单独缓存该 API 请求。
 */
import {
  loadBaseComponents,
  loadStoreBaseComponents,
  loadTenantComponents,
  setActiveDomain,
  getAllDefinitions,
  hydrateDefinitions,
} from '~/utils/remoteComponentRegistry'
import { normalizeLocale, parseLocalePath } from '~/utils/localeController'
import { getRuntimeTailwindStore, shouldCompileRuntimeTailwind } from '~/composable/useCmsRuntimeTailwindStore'

export default defineNuxtPlugin(async () => {
  if (import.meta.server) {
    const reqHeaders = useRequestHeaders(['host', 'x-forwarded-host', 'x-forwarded-proto', 'lang', 'x-soho-route-lang'])
    const route = useRoute()
    const routePath = route.fullPath || '/'
    const routeLang = normalizeLocale(
      parseLocalePath(routePath).locale
      || reqHeaders['x-soho-route-lang']
      || reqHeaders.lang
      || 'zh',
    )
    const domain = String(reqHeaders['x-forwarded-host'] || reqHeaders.host || '').split(',')[0].trim()
    const componentHeaders = {
      ...reqHeaders,
      lang: routeLang,
      'x-soho-route-lang': routeLang,
      'x-soho-route-path': routePath,
    }
    setActiveDomain(domain, routeLang)
    // 只加载供 SSR 使用，不写入 useState，不进入 __NUXT_DATA__
    await Promise.all([
      loadBaseComponents('', componentHeaders),
      loadStoreBaseComponents('', componentHeaders),
      loadTenantComponents('', componentHeaders),
    ])

    // 页面级合并编译：把所有组件的 htmlTemplate 合并成一次 Tailwind 编译，
    // 消除各组件单独编译时 daisyUI 基础样式的 ~95% 重复注入。
    const store = getRuntimeTailwindStore()
    if (store.mergedCss === null) {
      const htmlTemplates = getAllDefinitions()
        .map(d => (d.htmlTemplate || '').trim())
        .filter(html => html && shouldCompileRuntimeTailwind(html))
      const uniqueHtmls = [...new Set(htmlTemplates)]
      if (uniqueHtmls.length > 0) {
        try {
          const resp = await $fetch<{ css?: string }>('/api/cms/custom-tailwind', {
            method: 'POST',
            body: { html: uniqueHtmls.join('\n') },
          })
          store.mergedCss = resp?.css || ''
        } catch (e) {
          console.warn('[cmsComponents] merged tailwind compile failed, falling back to per-component compile:', e)
          store.mergedCss = null // 保持 null，各组件降级为独立编译
        }
      } else {
        store.mergedCss = '' // 无需 tailwind，标记为空
      }
    }

    return
  }

  // 客户端：从 Nuxt state (payload) 直接注水当前页面用到的组件定义，从而避免客户端请求 /api/cms/components 全量接口
  const ssrUsedDefinitions = useState<Record<string, any>>('cmsUsedDefinitions')
  if (ssrUsedDefinitions.value && Object.keys(ssrUsedDefinitions.value).length > 0) {
    const list = Object.values(ssrUsedDefinitions.value)
    hydrateDefinitions(list)
    console.log(`[CMS Plugins] 客户端成功通过 payload 注水 ${list.length} 个组件定义，跳过网络请求。`)
  } else {
    // 兜底情况（例如一些没有使用 Schema 定义的纯本地页面），不发网络请求。
    console.log('[CMS Plugins] 客户端未检测到 Schema 组件使用记录，跳过组件定义请求。')
  }

  const routeLocale = normalizeLocale(
    parseLocalePath(`${window.location.pathname}${window.location.search}${window.location.hash}`).locale
    || document.documentElement.lang
    || 'zh',
  )
  setActiveDomain(window.location.host, routeLocale)
})
